/**
 * Allied Brass Bundle Builder Web Component
 * Orchestrates bundle selection on PDP recommendation cards.
 * Manages checkbox selections, live bundle pricing, and multi-item add-to-cart
 * with _bundle_* line item properties for Shopify Function discount.
 *
 * Trade customers are excluded from bundle UI via both:
 * 1. Liquid-side: <ab-bundle-builder> not rendered for trade customers
 * 2. JS-side: runtime window.ABTrade guard (defense-in-depth for CDN caching)
 *
 * @extends ABBaseComponent
 */
import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABBundleBuilder extends ABBaseComponent {
  connectedCallback() {
    // Defense-in-depth trade guard: hide if trade customer despite Liquid check
    if (window.ABTrade?.isTradeCustomer) {
      this.style.display = 'none';
      return;
    }

    // Parse configuration from data attributes
    this._mainVariantId = this.dataset.mainVariantId;
    this._mainPrice = parseInt(this.dataset.mainPrice, 10) || 0;
    this._discountPercent = parseInt(this.dataset.discountPercent, 10) || 10;
    this._minItems = parseInt(this.dataset.minItems, 10) || 2;
    this._minTotal = parseInt(this.dataset.minTotal, 10) || 10000; // cents

    // Generate unique bundle ID
    this._bundleId = `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

    // Cache DOM refs for summary bar
    this._summaryEl = this.$('[data-bundle-summary]');
    this._buyBtn = this.$('[data-bundle-buy]');
    this._originalEl = this.$('[data-bundle-original]');
    this._totalEl = this.$('[data-bundle-total]');
    this._savingsEl = this.$('[data-bundle-savings]');
    this._countEl = this.$('[data-bundle-count]');
    this._minimumEl = this.$('[data-bundle-minimum]');

    // Track selections: Map<productHandle, { variantId, price, checked }>
    this._selections = new Map();

    // Initialize selections from existing checkboxes
    this._initSelections();

    // Event listeners (bound for cleanup)
    this._onSwatchSelected = this._onSwatchSelected.bind(this);
    this._onCheckboxChange = this._onCheckboxChange.bind(this);
    this._onBuyAll = this._onBuyAll.bind(this);

    // Listen for swatch:selected (bubbles from child product cards)
    this.addEventListener('swatch:selected', this._onSwatchSelected);

    // Listen for checkbox changes (event delegation)
    this.addEventListener('change', this._onCheckboxChange);

    // Buy All button
    if (this._buyBtn) {
      this._buyBtn.addEventListener('click', this._onBuyAll);
    }

    // Listen for PDP variant change via MutationObserver on input[name="id"]
    this._setupMainVariantObserver();

    // Initial summary update
    this._updateSummary();
  }

  disconnectedCallback() {
    // Remove event listeners
    this.removeEventListener('swatch:selected', this._onSwatchSelected);
    this.removeEventListener('change', this._onCheckboxChange);
    if (this._buyBtn) {
      this._buyBtn.removeEventListener('click', this._onBuyAll);
    }

    // Disconnect MutationObserver
    if (this._variantObserver) {
      this._variantObserver.disconnect();
      this._variantObserver = null;
    }

    // Clear state
    this._selections.clear();
  }

  // ─── Initialization ────────────────────────────────

  /**
   * Scan all bundle checkboxes and populate the _selections Map.
   */
  _initSelections() {
    const checkboxes = this.$$('[data-bundle-checkbox]');
    checkboxes.forEach(cb => {
      const handle = cb.dataset.productHandle;
      if (handle) {
        this._selections.set(handle, {
          variantId: cb.dataset.variantId,
          price: parseInt(cb.dataset.price, 10) || 0,
          checked: cb.checked
        });
      }
    });
  }

  /**
   * Observe the PDP's hidden variant input for changes.
   * When the customer changes finish/size on the main product,
   * update _mainVariantId and _mainPrice.
   */
  _setupMainVariantObserver() {
    const form = document.querySelector('form[action*="/cart/add"]');
    const variantInput = form?.querySelector('input[name="id"]');
    if (!variantInput) return;

    this._variantObserver = new MutationObserver(() => {
      this._mainVariantId = variantInput.value;

      // Read updated sale price (not compare-at) from the PDP price display
      // .price--selling contains only the actual selling price; falls back to [data-product-price]
      const salePriceEl = document.querySelector('.price--selling') || document.querySelector('[data-product-price]');
      if (salePriceEl) {
        const priceText = salePriceEl.textContent.replace(/[^0-9.]/g, '');
        const parsed = parseFloat(priceText);
        if (!isNaN(parsed)) {
          this._mainPrice = Math.round(parsed * 100);
        }
      }

      this._updateSummary();
    });

    this._variantObserver.observe(variantInput, {
      attributes: true,
      attributeFilter: ['value']
    });
  }

  // ─── Event Handlers ────────────────────────────────

  /**
   * Handle swatch:selected from child product cards.
   * Updates the selection's variant ID, price, and checkbox data attributes.
   */
  _onSwatchSelected(e) {
    const { productHandle, variantId } = e.detail;
    if (!productHandle || !this._selections.has(productHandle)) return;

    const selection = this._selections.get(productHandle);
    selection.variantId = variantId;

    // Read price from the clicked swatch dot's data-variant-price attribute
    const card = this.querySelector(`ab-product-card[data-product-handle="${productHandle}"]`);
    if (card) {
      const dot = card.querySelector(`[data-variant-id="${variantId}"]`);
      if (dot && dot.dataset.variantPrice) {
        selection.price = parseInt(dot.dataset.variantPrice, 10) || selection.price;
      }

      // Keep the checkbox data attributes in sync
      const checkbox = card.querySelector('[data-bundle-checkbox]');
      if (checkbox) {
        checkbox.dataset.variantId = variantId;
        checkbox.dataset.price = String(selection.price);
      }

      // Update the card-level data-price for consistency
      card.dataset.price = String(selection.price);
    }

    this._updateSummary();
  }

  /**
   * Handle checkbox change events (event delegation).
   */
  _onCheckboxChange(e) {
    const checkbox = e.target.closest('[data-bundle-checkbox]');
    if (!checkbox) return;

    const handle = checkbox.dataset.productHandle;
    if (handle && this._selections.has(handle)) {
      this._selections.get(handle).checked = checkbox.checked;
      this._updateSummary();
    }
  }

  // ─── Pricing Logic ─────────────────────────────────

  /**
   * Recalculate bundle pricing and update the summary bar DOM.
   * Main product is always included (no checkbox, always part of bundle).
   */
  _updateSummary() {
    // Collect checked rec items
    const checkedItems = [];
    for (const [, sel] of this._selections) {
      if (sel.checked) {
        checkedItems.push(sel);
      }
    }

    // Always include the main product
    const allItems = [
      { variantId: this._mainVariantId, price: this._mainPrice },
      ...checkedItems
    ];

    const itemCount = allItems.length;
    const subtotal = allItems.reduce((sum, item) => sum + item.price, 0);

    const meetsMinItems = itemCount >= this._minItems;
    const meetsMinTotal = subtotal >= this._minTotal;
    const eligible = meetsMinItems && meetsMinTotal;

    const discount = eligible ? Math.round(subtotal * this._discountPercent / 100) : 0;
    const bundleTotal = subtotal - discount;

    // Update DOM
    if (this._countEl) {
      this._countEl.textContent = String(itemCount);
    }

    if (this._originalEl) {
      this._originalEl.textContent = this._formatMoney(subtotal);
      if (discount > 0) {
        this._originalEl.classList.add('has-discount');
      } else {
        this._originalEl.classList.remove('has-discount');
      }
    }

    if (this._totalEl) {
      this._totalEl.textContent = this._formatMoney(bundleTotal);
    }

    if (this._savingsEl) {
      if (eligible && discount > 0) {
        this._savingsEl.textContent = `Save ${this._formatMoney(discount)} (${this._discountPercent}% off)`;
      } else {
        this._savingsEl.textContent = '';
      }
    }

    // Enable buy button if at least main + 1 rec checked
    if (this._buyBtn) {
      this._buyBtn.disabled = itemCount < 2;
    }

    // Show/hide the summary bar
    if (this._summaryEl) {
      this._summaryEl.hidden = false;
    }

    // Show minimum threshold message when items present but total too low
    if (this._minimumEl) {
      if (itemCount >= 2 && !meetsMinTotal) {
        this._minimumEl.hidden = false;
      } else {
        this._minimumEl.hidden = true;
      }
    }
  }

  /**
   * Format cents as a dollar string (e.g., 27600 -> "$276.00").
   */
  _formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ─── Add to Cart ───────────────────────────────────

  /**
   * Add all checked bundle items + main product to cart.
   * Uses the existing cart drawer's addToCart method for
   * Section Rendering API updates and drawer opening.
   */
  async _onBuyAll() {
    if (!this._buyBtn || this._buyBtn.disabled) return;

    // Collect all checked items + main product
    const checkedItems = [];
    for (const [, sel] of this._selections) {
      if (sel.checked) {
        checkedItems.push(sel);
      }
    }

    const allItems = [
      { variantId: this._mainVariantId, price: this._mainPrice },
      ...checkedItems
    ];

    if (allItems.length < 2) return;

    // Build items array with bundle line item properties
    const items = allItems.map(item => ({
      id: parseInt(item.variantId, 10),
      quantity: 1,
      properties: {
        '_bundle_source': 'pdp_complete_your_bathroom',
        '_bundle_discount': String(this._discountPercent),
        '_bundle_id': this._bundleId,
        '_rec_source': 'pdp_bundle'
      }
    }));

    // Set button to loading state
    const originalText = this._buyBtn.textContent;
    this._buyBtn.disabled = true;
    this._buyBtn.textContent = 'Adding...';

    try {
      // Use the existing cart drawer's addToCart method
      const cartDrawer = document.querySelector('ab-cart-drawer');
      if (cartDrawer && typeof cartDrawer.addToCart === 'function') {
        await cartDrawer.addToCart(items);
      } else {
        // Fallback: direct fetch if cart drawer not found
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.description || 'Failed to add bundle to cart');
        }

        // Dispatch cart:open to trigger drawer
        document.dispatchEvent(new CustomEvent('cart:open'));
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'bundle-builder' }
        }));
      }

      // Success state
      this._buyBtn.textContent = 'Added!';
      setTimeout(() => {
        this._buyBtn.disabled = false;
        this._buyBtn.textContent = originalText;
      }, 1500);

      // Generate new bundle ID for next potential bundle
      this._bundleId = `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

      // Push analytics event
      const subtotal = allItems.reduce((sum, item) => sum + item.price, 0);
      const discount = Math.round(subtotal * this._discountPercent / 100);
      this._pushDataLayer('bundle_add_to_cart', {
        bundle_item_count: items.length,
        bundle_subtotal: subtotal,
        bundle_discount: discount
      });

    } catch (err) {
      console.error('[ABBundleBuilder] _onBuyAll error:', err);

      // Error state
      this._buyBtn.textContent = 'Error \u2014 try again';
      setTimeout(() => {
        this._buyBtn.disabled = false;
        this._buyBtn.textContent = originalText;
      }, 2000);
    }
  }

  // ─── Analytics ─────────────────────────────────────

  _pushDataLayer(event, data) {
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({ event, ...data });
    }
  }
}

defineComponent('ab-bundle-builder', ABBundleBuilder);
